import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { STORAGE_PREFIX } from '../constants';

// ---------------------------------------------------------------------------
// Tracks which admin pages the user has visited. Used by sidebar beacons
// to highlight features the user hasn't explored yet.
// ---------------------------------------------------------------------------

let listeners: Array<() => void> = [];
let version = 0;
function subscribe(cb: () => void) {
  listeners = [...listeners, cb];
  return () => { listeners = listeners.filter((l) => l !== cb); };
}
function getSnapshot() { return version; }
function emitChange() { version++; listeners.forEach((l) => l()); }

function visitedKey(uid: string) {
  return `${STORAGE_PREFIX}:${uid}:visitedPages`;
}

function readVisited(uid: string): string[] {
  try {
    const raw = window.localStorage.getItem(visitedKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeVisited(uid: string, pages: string[]) {
  try {
    window.localStorage.setItem(visitedKey(uid), JSON.stringify(pages));
  } catch {
    // silently ignore
  }
}

export function useFeatureDiscovery(uid: string | undefined) {
  const location = useLocation();
  const storeVersion = useSyncExternalStore(subscribe, getSnapshot);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void storeVersion;

  // Auto-record current page visit
  useEffect(() => {
    if (!uid) return;
    const path = location.pathname.replace('/admin', '');
    const current = readVisited(uid);
    if (!current.includes(path)) {
      writeVisited(uid, [...current, path]);
      emitChange();
    }
  }, [uid, location.pathname]);

  const hasVisited = useCallback(
    (path: string) => {
      if (!uid) return true; // no UID = no beacons
      return readVisited(uid).includes(path);
    },
    [uid],
  );

  return { hasVisited };
}
